import styles from "./Subscribe.module.css";
import React, { useCallback, useMemo } from "react";
import type { SubscribeCatalogProduct } from "../types";
import { sortSubscribeProductsByPriceAsc } from "../utils/productCatalog";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../../../config/firebase";
import { formatPeriodEnd } from "../utils/formatPeriodEnd";

export type StatusProps = {
  catalogProducts: SubscribeCatalogProduct[];
  hasActiveStripe: boolean;
  stripeStatusLoading: boolean;
  activePriceId?: string;
  currentPeriodEnd?: string;
  cancelAt?: string;
};

export const SubscriptionStatus = ({
  catalogProducts,
  hasActiveStripe,
  stripeStatusLoading,
  activePriceId,
  currentPeriodEnd,
  cancelAt,
}: StatusProps) => {
  const openBillingPortal = useCallback(async () => {
    try {
      const returnUrl = window.location.href;
      const fn = httpsCallable(
        getFunctions(app, "europe-west1"),
        "createBillingPortalForUser"
      );
      const result = await fn({ return_url: returnUrl });
      const url = (result.data as { url?: string })?.url;
      if (url) {
        window.location.assign(url);
      }
    } catch (err) {
      console.warn("createBillingPortalForUser failed:", err);
    }
  }, []);

  const periodLabel = formatPeriodEnd(currentPeriodEnd);

  const productsSorted = useMemo(() => {
    const list = Array.isArray(catalogProducts) ? catalogProducts : [];
    return sortSubscribeProductsByPriceAsc(list).filter((p) => p.default_price);
  }, [catalogProducts]);

  const currentCatalogProduct = useMemo(() => {
    if (!activePriceId) return null;
    return (
      productsSorted.find((p) => p.default_price === activePriceId) ?? null
    );
  }, [activePriceId, productsSorted]);

  return (
    <>
      {hasActiveStripe && (
        <div className={styles.currentPlanPanel}>
          <span className={styles.currentPlanTitle}>Your subscription</span>
          {stripeStatusLoading ? (
            <p className={styles.mutedLine}>Checking your plan…</p>
          ) : (
            <>
              <p className={styles.currentPlanName}>
                {currentCatalogProduct?.name ?? "Active subscription-details"}
                {currentCatalogProduct?.price ? (
                  <span className={styles.mutedInline}>
                    {" "}
                    — {currentCatalogProduct.price} / month
                  </span>
                ) : null}
              </p>
              {periodLabel ? (
                <p className={styles.mutedLine}>
                  {cancelAt != null
                    ? `Access continues until ${periodLabel}, then your subscription ends.`
                    : `Current period renews on ${periodLabel}.`}
                </p>
              ) : null}
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => void openBillingPortal()}
              >
                Manage billing
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
};
